import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';

import Dashboard from '../Dashboard';
import AccountDetails from '../AccountDetails';
import Delegate from '../Delegate';
import OrgChart from '../OrgChart';
import Reassign from '../Reassign';
import Renew from '../Renew';
import Terminate from '../Terminate';
import UpdateProfile from '../UpdateProfile';
import Validate from '../Validate';
import * as actions from '../../../store/uar/actions';
import * as ancillaryActions from '../../../store/ancillary/actions';
import * as lifecycleWrapper from '../util/lifecycleWrapper';
import axiosInstance from '../../../services/axios/axios';
import { UAR_CONSTANTS } from '../util/constants';
import ReviewEmployeeAccess from '../../AnnualUserAccessReview/views/Review/ReviewEmployeeAccess/ReviewEmployeeAccess';

/**
 * User Access Review Scaffolding
 * Primary logic for UAR which distributes data to individual pages.
 * @param {*} props - inherited properties
 */
const Scaffold = props => {
	/**
	 * Extract the routing information from the inherited props
	 */
	const { history, location, match } = props;

	/**
	 * action dispatcher
	 */
	const dispatch = useDispatch();

	/**
	 * [Redux State] - information about the currently logged in user
	 */
	const loggedInUser = useSelector(state => state.uar.adalProfile);

	/**
	 * [Redux State] - bulk list of targets to validate
	 */
	const bulkSelections = useSelector(state => state.uar.bulkSelections);

	/**
	 * (set)BulkFlow - indicates a bulk action has been invoked
	 */
	const [bulkFlow, setBulkFlow] = useState(false);

	/**
	 * (set)PerformActions - determines permissions for the currently logged in user
	 */
	const [canPerformActions, setCanPerformActions] = useState(null);

	/**
	 * (set)Error - flag when errors detected
	 */
	const [error, setError] = useState(false);

	/**
	 * (set)ErrorMessage - custom override for error page HOC output
	 */
	const [errorMessage, setErrorMessage] = useState('');

	/**
	 * (set)EsoFiId - for single-flow, stores the target account's T_ESO FI_ID
	 */
	const [esoFiId, setEsoFiId] = useState(null);

	/**
	 * (set)Loading - used to display loading animation
	 */
	const [loading, setLoading] = useState(true);

	/**
	 * (set)LoggedInEsoFiId - the T_ESO FI_ID for the currently logged in user
	 */
	const [loggedInEsoFiId, setLoggedInEsoFiId] = useState(null);

	/**
	 * (set)ProfileDetails - profile information for the target account
	 */
	const [profileDetails, setProfileDetails] = useState(null);

	/**
	 * (set)ProfileApplications - applications information for the target account
	 */
	const [profileApplications, setProfileApplications] = useState(null);

	/**
	 * (set)ProfileMobileDevices - mobile phone information for the target account
	 */
	const [profileMobileDevices, setProfileMobileDevices] = useState(null);

	/**
	 * (set)ProfileBadges - badge information for the target account
	 */
	const [profileBadges, setProfileBadges] = useState(null);

	/**
	 * (set)ProfileServiceAccounts - service account information for the target account
	 */
	const [profileServiceAccounts, setProfileServiceAccounts] = useState(null);

	/**
	 * (set)TargetProfileDetails - profile information for the target account
	 */
	const [targetProfileDetails, setTargetProfileDetails] = useState(null);

	/**
	 * (set)UsersFound - local check to see if either single or bulk user flow is detected
	 */
	const [usersFound, setUsersFound] = useState(false);

	/**
	 * (set)UserStaff - stores information about the direct reports for the current user
	 */
	const [userStaff, setUserStaff] = useState();

	/**
	 * (set)AccountTypesAllowed - lists the accounts types you are allowed to perform actions on a given page
	 * Defaulted to all.
	 */
	const [accountTypesAllowed, setAccountTypesAllowed] = useState([
		UAR_CONSTANTS.EMPLOYEE_KEY,
		UAR_CONSTANTS.NON_EMPLOYEE_KEY,
		UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
		UAR_CONSTANTS.SERVICE_ACCOUNT_KEY,
	]);

	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_REASSIGN:
			case UAR_CONSTANTS.PAGE_RENEW:
				setAccountTypesAllowed([
					UAR_CONSTANTS.NON_EMPLOYEE_KEY,
					UAR_CONSTANTS.CONTINGENT_WORKER_KEY,
					UAR_CONSTANTS.SERVICE_ACCOUNT_KEY,
				]);
				break;
			case UAR_CONSTANTS.PAGE_VALIDATE:
				setAccountTypesAllowed([UAR_CONSTANTS.EMPLOYEE_KEY]);
				break;
			default:
				break;
		}
	}, [props.pageID]);

	/**
	 * Get User Staff
	 * check on Redux store for information on the currently logged in user and query API for more information
	 * triggers on state change for `loggedInUser`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_DELEGATE:
				if (loggedInUser) {
					lifecycleWrapper.queryGetUserStaffByUpn(
						loggedInUser,
						onGetUserStaffQuery,
						onGetUserStaffResponse,
						onGetUserStaffSuccess,
						onGetUserStaffFailure,
						onGetUserStaffError,
						onGetUserStaffComplete,
						dispatch
					);
				}
				break;
			default:
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInUser]);

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when API response is received (pre-processing)
	 * @param {*} data response from the API call
	 */
	const onGetUserStaffResponse = data => {
		for (let member in data.staff) {
			data.staff[member].selected = false;
			//account details, delegate only - start
			data.staff[member].key = data.staff[member].esoFiId;
			data.staff[
				member
			].value = `${data.staff[member].lastName}, ${data.staff[member].firstName}`;
			//account details, delegate only - end
		}
		return data;
	};

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when API request is sent
	 */
	const onGetUserStaffQuery = () => {
		setLoading(true);
		if (props.pageID === UAR_CONSTANTS.PAGE_DASHBOARD) {
			dispatch(actions.resetBulkSelection());
		}
	};

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when request is completed
	 */
	const onGetUserStaffComplete = () => {};

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when API response is received and is successful (200)
	 * @param {*} data response from the API call
	 * @param {*} messages messages to display from the API
	 */
	const onGetUserStaffSuccess = (data, messages) => {
		dispatch(ancillaryActions.clearMessages());
		setLoading(false);
		setUserStaff(data);
		dispatch(actions.setLoggedInUser({ payload: data.loggedInUser }));
		if (data.loggedInUser) {
			setLoggedInEsoFiId(data.loggedInUser.esoFiId);
		}
	};

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when API response is received and is unsuccessful
	 * @param {*} error error response data from the API call
	 */
	const onGetUserStaffError = error => {
		setError(true);
		setLoading(false);
	};

	/**
	 * Get User Staff (useEffect) function pointer
	 * invoked when API request is unsuccessful
	 * @param {*} messages messages to display
	 */
	const onGetUserStaffFailure = messages => {
		dispatch(ancillaryActions.clearMessages());
		if (messages) {
			messages.forEach(m => dispatch(ancillaryActions.addMessage(m)));
		}
		setError(true);
		setLoading(false);
	};

	/**
	 * Get User Profile Details
	 * check on Redux store for information on the currently logged in user and query API for more information
	 * triggers on state change for `loggedInUser`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
				break;
			default:
				if (loggedInUser) {
					lifecycleWrapper.queryGetUserProfileByUpn(
						loggedInUser,
						onGetUserProfileQuery,
						onGetUserProfileResponse,
						onGetUserProfileSuccess,
						onGetUserProfileFailure,
						onGetUserProfileError,
						onGetUserProfileComplete,
						dispatch
					);
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInUser]);

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when API response is received (pre-processing)
	 * @param {*} data response from the API call
	 */
	const onGetUserProfileResponse = data => {
		for (let member in data.staff) {
			data.staff[member].selected = false;
			data.staff[member].key = data.staff[member].esoFiId;
			data.staff[
				member
			].value = `${data.staff[member].lastName}, ${data.staff[member].firstName}`;
		}
		return data;
	};

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when API request is sent
	 */
	const onGetUserProfileQuery = () => {
		setLoading(true);
		if (props.pageID === UAR_CONSTANTS.PAGE_DASHBOARD) {
			dispatch(actions.resetBulkSelection());
		}
	};

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when request is completed
	 */
	const onGetUserProfileComplete = () => {};

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when API response is received and is successful (200)
	 * @param {*} data response from the API call
	 * @param {*} messages messages to display from the API
	 */
	const onGetUserProfileSuccess = (data, messages) => {
		setLoading(false);
		dispatch(actions.setLoggedInUser({ payload: data.loggedInUser }));
		if (data.loggedInUser) {
			setLoggedInEsoFiId(data.loggedInUser.esoFiId);
		}
	};

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when API response is received and is unsuccessful
	 * @param {*} error error response data from the API call
	 */
	const onGetUserProfileError = error => {
		setError(true);
		//delegate only - start
		setLoading(false);
		//delegate only - end
	};

	/**
	 * Get User Profile Details (useEffect) function pointer
	 * invoked when API request is unsuccessful
	 * @param {*} messages messages to display
	 */
	const onGetUserProfileFailure = messages => {
		dispatch(ancillaryActions.clearMessages());
		if (messages) {
			messages.forEach(m => dispatch(ancillaryActions.addMessage(m)));
		}
		setError(true);
		setLoading(false);
	};

	/**
	 * check for users to view
	 * triggers on state change for `bulkSelections`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
				break;
			default:
				const result = lifecycleWrapper.validateUsersFound(
					props.location.search,
					bulkSelections,
					'view'
				);
				if (result.valid) {
					setUsersFound(true);
				} else {
					setError(true);
					setErrorMessage(result.errorMessage);
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bulkSelections]);

	/**
	 * Get and Set Profile Information
	 * Profile details, applications, mobile devices, badges, service accounts
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_ORG_CHART:
			case UAR_CONSTANTS.PAGE_VALIDATE:
				break;
			default:
				if (usersFound) {
					setLoading(true);

					let newDetails = { ...profileDetails };
					let newApplications = { ...profileApplications };
					let newMobileDevices = { ...profileMobileDevices };
					let newBadges = { ...profileBadges };
					let newServiceAccounts = { ...profileServiceAccounts };

					const query = new URLSearchParams(props.location.search);
					if (query.has('esoFiId')) {
						let requestGetUserInfo = axiosInstance.get(
							'/api/UserAccessReview/GetUserInfo/' +
								query.get('esoFiId')
						);
						let requestGetUserApps = axiosInstance.get(
							'/api/UserAccessReview/GetUserApps/' +
								query.get('esoFiId')
						);
						let requestGetUserMobileDevices = axiosInstance.get(
							'/api/UserAccessReview/GetUserMobileDevices/' +
								query.get('esoFiId')
						);
						let requestGetUserBadges = axiosInstance.get(
							'/api/UserAccessReview/GetUserBadges/' +
								query.get('esoFiId')
						);
						let requestGetUserServiceAccounts = axiosInstance.get(
							'/api/UserAccessReview/GetUserServiceAccounts/' +
								query.get('esoFiId')
						);
						let allMessages = [];
						axios
							.all([
								requestGetUserInfo,
								requestGetUserApps,
								requestGetUserMobileDevices,
								requestGetUserBadges,
								requestGetUserServiceAccounts,
							])
							.then(
								axios.spread((...responses) => {
									const responseGetUserInfo = responses[0];
									const responseGetUserApps = responses[1];
									const responseGetUserMobileDevices =
										responses[2];
									const responseGetUserBadges = responses[3];
									const responseGetUserServiceAccounts =
										responses[4];

									switch (responseGetUserInfo.status) {
										case 200:
											if (
												responseGetUserInfo.data
													.responseCode
											) {
												const responseCode =
													responseGetUserInfo.data
														.responseCode;
												let data =
													responseGetUserInfo.data
														.payload;
												const messages =
													responseGetUserInfo.data
														.messages;
												allMessages.concat(messages);
												switch (responseCode) {
													case 200:
														newDetails = data;
														break;
													case 206:
														dispatch(
															ancillaryActions.clearMessages()
														);
														messages.forEach(m =>
															dispatch(
																ancillaryActions.addMessage(
																	m
																)
															)
														);
														setError(true);
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}

									switch (responseGetUserApps.status) {
										case 200:
											if (
												responseGetUserApps.data
													.responseCode
											) {
												const responseCode =
													responseGetUserApps.data
														.responseCode;
												let data =
													responseGetUserApps.data
														.payload;
												const messages =
													responseGetUserApps.data
														.messages;
												allMessages.concat(messages);
												switch (responseCode) {
													case 200:
														newApplications = data;
														break;
													case 206:
														dispatch(
															ancillaryActions.clearMessages()
														);
														messages.forEach(m =>
															dispatch(
																ancillaryActions.addMessage(
																	m
																)
															)
														);
														setError(true);
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}

									switch (
										responseGetUserMobileDevices.status
									) {
										case 200:
											if (
												responseGetUserMobileDevices
													.data.responseCode
											) {
												const responseCode =
													responseGetUserMobileDevices
														.data.responseCode;
												let data =
													responseGetUserMobileDevices
														.data.payload;
												const messages =
													responseGetUserMobileDevices
														.data.messages;
												allMessages.concat(messages);
												switch (responseCode) {
													case 200:
														newMobileDevices = data;
														break;
													case 206:
														dispatch(
															ancillaryActions.clearMessages()
														);
														messages.forEach(m =>
															dispatch(
																ancillaryActions.addMessage(
																	m
																)
															)
														);
														setError(true);
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}

									switch (responseGetUserBadges.status) {
										case 200:
											if (
												responseGetUserBadges.data
													.responseCode
											) {
												const responseCode =
													responseGetUserBadges.data
														.responseCode;
												let data =
													responseGetUserBadges.data
														.payload;
												const messages =
													responseGetUserBadges.data
														.messages;
												allMessages.concat(messages);
												switch (responseCode) {
													case 200:
														newBadges = data;
														break;
													case 206:
														dispatch(
															ancillaryActions.clearMessages()
														);
														messages.forEach(m =>
															dispatch(
																ancillaryActions.addMessage(
																	m
																)
															)
														);
														setError(true);
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}

									switch (
										responseGetUserServiceAccounts.status
									) {
										case 200:
											if (
												responseGetUserServiceAccounts
													.data.responseCode
											) {
												const responseCode =
													responseGetUserServiceAccounts
														.data.responseCode;
												let data =
													responseGetUserServiceAccounts
														.data.payload;
												const messages =
													responseGetUserServiceAccounts
														.data.messages;
												allMessages.concat(messages);
												switch (responseCode) {
													case 200:
														newServiceAccounts =
															data;
														break;
													case 206:
														dispatch(
															ancillaryActions.clearMessages()
														);
														messages.forEach(m =>
															dispatch(
																ancillaryActions.addMessage(
																	m
																)
															)
														);
														setError(true);
														break;
													default:
														break;
												}
											}
											break;

										default:
											break;
									}

									setProfileDetails(newDetails);
									setProfileApplications(newApplications);
									setProfileMobileDevices(newMobileDevices);
									setProfileBadges(newBadges);
									setProfileServiceAccounts(
										newServiceAccounts
									);
								})
							)
							.then(() => {
								setLoading(false);
								allMessages.forEach(m => {
									dispatch(ancillaryActions.addMessage(m));
								});
							})
							.catch(errors => {
								setLoading(false);
								setError(true);
							});
					}
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.location.search, usersFound]);

	/**
	 * Get User Info By ESO FI_ID
	 * check on URL for ESO FI_ID and call API for more information on the target user
	 * triggers on state change for `props.location.search`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DELEGATE:
			case UAR_CONSTANTS.PAGE_REASSIGN:
				if (usersFound) {
					const queryParams = new URLSearchParams(
						props.location.search
					);
					lifecycleWrapper.queryGetUserInfoByEsoFiId(
						queryParams,
						onGetUserInfoInfoQuery,
						onGetUserInfoInfoResponse,
						onGetUserInfoInfoSuccess,
						onGetUserInfoInfoFailure,
						onGetUserInfoInfoError,
						onGetUserInfoInfoComplete
					);
				}
				break;
			default:
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.location.search, usersFound]);

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API request is sent
	 */
	const onGetUserInfoInfoQuery = () => {
		setLoading(true);
	};

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API request is received
	 */
	const onGetUserInfoInfoResponse = data => {
		return data;
	};

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API request is completed
	 */
	const onGetUserInfoInfoComplete = () => {};

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API response is received and is successful (200)
	 * @param {*} data response from the API call
	 * @param {*} messages messages to display from the API
	 */
	const onGetUserInfoInfoSuccess = (data, messages) => {
		setLoading(false);
		dispatch(ancillaryActions.clearMessages());
		setTargetProfileDetails(data);
	};

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API response is received and is unsuccessful
	 * @param {*} error error response data from the API call
	 */
	const onGetUserInfoInfoError = error => {
		setError(true);
		dispatch(
			ancillaryActions.addMessage({
				Key: 3,
				Value: 'No data returned.',
			})
		);
	};

	/**
	 * Get User Info By ESO FI_ID (useEffect) function pointer
	 * invoked when API request is unsuccessful
	 * @param {*} messages messages to display
	 */
	const onGetUserInfoInfoFailure = messages => {
		dispatch(ancillaryActions.clearMessages());
		if (messages) {
			messages.forEach(m => dispatch(ancillaryActions.addMessage(m)));
		}
		setError(true);
		setLoading(false);
	};

	/**
	 * Can Perform Actions Check
	 * verifies if the currently logged in user can perform actions on the page
	 * triggers once profile information and eso FI_ID are updated
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_ORG_CHART:
				setCanPerformActions(true);
				break;
			case UAR_CONSTANTS.PAGE_VALIDATE:
				break;
			case UAR_CONSTANTS.PAGE_RENEW:
				if (profileDetails && loggedInEsoFiId) {
					setCanPerformActions(
						lifecycleWrapper.checkPermissions(
							profileDetails,
							loggedInEsoFiId,
							null,
							true
						)
					);
				}
				break;
			default:
				if (profileDetails && loggedInEsoFiId) {
					setCanPerformActions(
						lifecycleWrapper.checkPermissions(
							profileDetails,
							loggedInEsoFiId,
							null,
							false
						)
					);
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInEsoFiId, profileDetails, bulkSelections]);

	/**
	 * Check Permissions
	 * triggered by changes to `canPerformActions`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_ORG_CHART:
			case UAR_CONSTANTS.PAGE_ACCOUNT_DETAILS:
				break;
			default:
				if (canPerformActions === false) {
					setError(true);
					setErrorMessage(
						'You do not have permission to perform this action.'
					);
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canPerformActions]);

	/**
	 * Set Flow
	 * check on URL for ESO FI_ID and call API for more information on the target user
	 * triggers on state change for `props.location.search`
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_ORG_CHART:
				break;
			default:
				const queryParams = new URLSearchParams(props.location.search);
				//Single flow
				if (lifecycleWrapper.checkForBulkFlow(queryParams)) {
					setBulkFlow(true);
				} else {
					dispatch(actions.resetBulkSelection());
					setEsoFiId(queryParams.get('esoFiId'));
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.location.search]);

	/**
	 * Check Account Types
	 * check for the allowed account types and whether the page supports it
	 * remove the bulk selections that do not match the allowed account types or error out if single-flow
	 */
	useEffect(() => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
			case UAR_CONSTANTS.PAGE_ORG_CHART:
				break;
			default:
				//single user flow
				if (accountTypesAllowed && profileDetails) {
					if (!accountTypesAllowed.includes(profileDetails.type)) {
						setError(true);
						setErrorMessage(
							'You cannot perform this action on this account type.'
						);
					}
				}
				//bulk flow
				if (accountTypesAllowed && bulkFlow) {
					let accountsRemoved = [];
					bulkSelections.forEach(member => {
						if (!accountTypesAllowed.includes(member.type)) {
							accountsRemoved.push(
								`${member.firstName} ${member.lastName}`
							);
							dispatch(
								actions.removeBulkSelection({
									payload: member,
								})
							);
						}
					});
					if (accountsRemoved.length > 0) {
						const action = getActionText();
						const accountTypes =
							getAccountTypeTextList().join(', ');
						const members = accountsRemoved.join(', ');
						dispatch(
							ancillaryActions.addMessage({
								Key: 2,
								Value: `This action, ${action}, is allowed for account type(s) ${accountTypes}.  The following accounts have been removed from your bulk request: ${members}`,
							})
						);
					}
				}
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profileDetails, bulkFlow, accountTypesAllowed]);

	/**
	 * get the text output for the action associated with the page
	 * @returns {string} action text
	 */
	const getActionText = () => {
		switch (props.pageID) {
			case UAR_CONSTANTS.PAGE_DASHBOARD:
				return UAR_CONSTANTS.PAGE_DASHBOARD_ACTION;
			case UAR_CONSTANTS.PAGE_ACCOUNT_DETAILS:
				return UAR_CONSTANTS.PAGE_ACCOUNT_DETAILS_ACTION;
			case UAR_CONSTANTS.PAGE_DELEGATE:
				return UAR_CONSTANTS.PAGE_DELEGATE_ACTION;
			case UAR_CONSTANTS.PAGE_REASSIGN:
				return UAR_CONSTANTS.PAGE_REASSIGN_ACTION;
			case UAR_CONSTANTS.PAGE_TERMINATE:
				return UAR_CONSTANTS.PAGE_TERMINATE_ACTION;
			case UAR_CONSTANTS.PAGE_RENEW:
				return UAR_CONSTANTS.PAGE_RENEW_ACTION;
			case UAR_CONSTANTS.PAGE_UPDATE_PROFILE:
				return UAR_CONSTANTS.PAGE_UPDATE_PROFILE_ACTION;
			case UAR_CONSTANTS.PAGE_VALIDATE:
				return UAR_CONSTANTS.PAGE_VALIDATE_ACTION;
			case UAR_CONSTANTS.PAGE_ORG_CHART:
				return UAR_CONSTANTS.PAGE_ORG_CHART_ACTION;
			default:
				return '';
		}
	};

	/**
	 * get the text output list for the allowed account types
	 * @returns {string[]} list of allowed account types
	 */
	const getAccountTypeTextList = () => {
		let types = [];
		accountTypesAllowed.forEach(type => {
			switch (type) {
				case UAR_CONSTANTS.CONTINGENT_WORKER_KEY:
					types.push(UAR_CONSTANTS.CONTINGENT_WORKER);
					break;
				case UAR_CONSTANTS.EMPLOYEE_KEY:
					types.push(UAR_CONSTANTS.EMPLOYEE);
					break;
				case UAR_CONSTANTS.NON_EMPLOYEE_KEY:
					types.push(UAR_CONSTANTS.NON_EMPLOYEE);
					break;
				default:
					break;
			}
		});
		return types;
	};

	/**
	 * unified props to pass to each page
	 * constructed from the variables above
	 */
	const pageProps = {
		history: history,
		location: location,
		match: match,
		dispatch: dispatch,
		loggedInUser: loggedInUser,
		error: error,
		setError: setError,
		errorMessage: errorMessage,
		setErrorMessage: setErrorMessage,
		loading: loading,
		setLoading: setLoading,
		userStaff: userStaff,
		loggedInEsoFiId: loggedInEsoFiId,
		usersFound: usersFound,
		targetProfileDetails: targetProfileDetails,
		profileDetails: profileDetails,
		profileApplications: profileApplications,
		profileMobileDevices: profileMobileDevices,
		profileBadges: profileBadges,
		profileServiceAccounts: profileServiceAccounts,
		canPerformActions: canPerformActions,
		setCanPerformActions: setCanPerformActions,
		bulkSelections: bulkSelections,
		bulkFlow: bulkFlow,
		esoFiId: esoFiId,
		setProfileDetails: setProfileDetails,
		setProfileApplications: setProfileApplications,
		setProfileMobileDevices: setProfileMobileDevices,
		setProfileBadges: setProfileBadges,
		setProfileServiceAccounts: setProfileServiceAccounts,
		accountTypesAllowed: accountTypesAllowed,
	};

	/**
	 * based on `props.pageID`, render the appropriate page
	 */
	let page = <Dashboard {...pageProps} />;
	switch (props.pageID) {
		case UAR_CONSTANTS.PAGE_DASHBOARD:
			page = <Dashboard {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_ACCOUNT_DETAILS:
			page = <AccountDetails {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_DELEGATE:
			page = <Delegate {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_ORG_CHART:
			page = <OrgChart {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_REASSIGN:
			page = <Reassign {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_RENEW:
			page = <Renew {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_TERMINATE:
			page = <Terminate {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_UPDATE_PROFILE:
			page = <UpdateProfile {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_VALIDATE:
			page = <Validate {...pageProps} />;
			break;
		case UAR_CONSTANTS.PAGE_REVIEW_EMPLOYEE_ACCESS:
			page = <ReviewEmployeeAccess {...pageProps} />;
		default:
			break;
	}

	return <>{page}</>;
};

export default Scaffold;
